<script>

import {axios} from "../../mixins";
import {Alerts} from '../../mixins/Alerts.js';
import Geography from '../Geography/Geography';

export default {

  name: 'FieldYearsForm',

  components: {Geography},

  mixins: [axios],

  props: {
    resource: {
      required: true,
      type: Object,
    },
    locked: {
      required: false,
      type: Boolean,
      default: false,
    },
    options: {
      required: false,
      type: Object,
      default: function () {
        return {};
      },
    },
    alerts: {
      required: false,
      type: Alerts,
    },
    admin: {
      type: Boolean,
      default: false,
      required: false,
    },
    mapboxToken: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      resourceForm: {},
      editMode: false,
      showSoilTest: false,
      editableClasses: 'bg-grey italic text-white border-b py-2 border-grey-light w-full px-3 leading-tight focus:outline-none focus:bg-grey-light text-md',
      editableClassesSelect: 'bg-grey italic text-white border-b border-grey-light w-full py-2 px-3 leading-tight focus:outline-none focus:ring',
      viewClassesSelect: 'appearance-none w-full py-2 text-grey-dark leading-tight bg-grey-dark',
      viewClasses: 'appearance-none w-full leading-tight py-2 pb-2 bg-transparent text-md',
      confirmMethod: 0,
      confirmMethods: {
        0: false,
        1: 'delete',
      },
      submitting: false,
    };
  },

  watch: {
    resource() {
      this.resourceForm = {...this.resource};
      this.editMode = false
      this.boundaryUpdating = false
    }
  },

  computed: {
    isCreating() {
      return this.resource.id === null
    }
  },

  created() {
    window.addEventListener("keydown", this.keyListener);

    if (this.isCreating) {
      this.setDefaults()
      this.editMode = true
    }

    this.resource.table = 'field_years'
    this.resourceForm = {...this.resource};
  },

  methods: {
    keyListener(event) {
      if (event.key === "e" && !this.editMode) {
        this.toggleEdit()
      }
    },

    submitResource() {
      this.submitting = true
      let dataToSubmit = {...this.resource}
      dataToSubmit.data = this.resourceForm

      // get boundary data from the map

      let boundaryData = this.$refs['boundary-input'].getValue()
      dataToSubmit.data.boundary = boundaryData.boundary
      dataToSubmit.data.size = boundaryData.size
      dataToSubmit.data.centroid = boundaryData.center

      if (this.isCreating) {
        this.$emit('store', dataToSubmit, 'field_years')
      } else {
        this.$emit('update', dataToSubmit, 'field_years')
      }
      this.submitting = false
    },

    deleteResource() {
      this.$emit('delete', this.resource, 'field_years')
    },

    toggleEdit() {
      if (!this.locked) {
        this.editMode = !this.editMode;
        this.resourceForm = {...this.resource};
      }
    },

    setDefaults() {
      if (this.resource.field_id) {
        this.get('field_years/create?field_id=' + this.resource.field_id + '&year=' + this.resource.year).then((response) => {
          let defaults = response.data.resource
          this.resource.boundary = defaults.boundary
          this.resource.size = defaults.size
          this.resource.centroid = defaults.centroid
          this.resource.field_type = defaults.field_type
          this.resource.is_owner = defaults.is_owner

          this.resourceForm = {...this.resource};
        })
      }
    },

    confirmOpen(method) {
      this.confirmMethod = method;
    },
    confirmClose(cancel) {
      if(cancel) {
        this.confirmMethod = 0;
        return;
      }
      switch (this.confirmMethod) {
        case 1:
          this.confirmMethod = 0;
          this.deleteResource();
          break;
        default:
          break;
      }
    },
  },
};

</script>

<template>
  <form @submit.prevent="submitResource" method="post">
    <div class="p-4 flex flex-row w-full">
      <div class="mr-2 w-full">
        <div class="text-2xl font-bold py-3 border-b-2 border-grey">
          {{ (resource.field_name ? resource.field_name : 'Crop Year') }} {{ resource.year }} Data
          <span class="float-right text-xl text-orange" v-if="!locked">
            <button id="toggle-field-year-edit" type="button" @click="toggleEdit()">
                <span v-show="editMode">
                  <i class="fal fa-window-close"></i>
                </span>
                <span v-show="!editMode">
                  <i class="fal fa-pen-square"></i>
                </span>
            </button>
          </span>
        </div>

        <div class="flex flex-col flex-wrap flex-1 my-1">
          <div class="flex flex-row">
            <div class="flex flex-row flex-wrap flex-1 my-1">
              <label for="is_new" class="font-semibold">Enrollment Status:</label>
              <select name="is_new"
                      id="is_new"
                      v-model="resourceForm.is_new"
                      :class="editMode ? editableClassesSelect : viewClassesSelect"
                      v-if="editMode">
                <option :value="true">New Field</option>
                <option :value="false">Re-Enroll</option>
              </select>
              <div v-else>{{
                  resourceForm.is_new === true ? 'New Field' : (resourceForm.is_new === false ? 'Re-Enroll' : '')
                }}
              </div>
            </div>
          </div>
        </div>
        <div class="text-red">
          {{ alerts.getError('is_new') }}
        </div>

        <!-- OPERATOR QUESTION -->
        <div class="flex flex-col flex-wrap flex-1 mb-3">
          <div class="font-semibold">
            What is your (the applicant's) relationship to this field?
          </div>
          <div class="flex flex-row" v-if="editMode">
            <div class="flex flex-row flex-wrap flex-1 my-1">
              <select name="is_owner"
                      id="is_owner"
                      v-model="resourceForm.is_owner"
                      :class="editMode ? editableClassesSelect : viewClassesSelect"
                      v-if="editMode">
                <option :value="true">Owner</option>
                <option :value="false">Rent/Lease</option>
              </select>
            </div>
          </div>
          <div v-else>
                    <span>
                      {{ resourceForm.is_owner ? 'Owner' : 'Rent/Lease' }}
                    </span>
          </div>
          <div class="text-red">
            {{ alerts.getError('is_owner') }}
          </div>
        </div>

        <div class="flex flex-col flex-wrap flex-1 my-1">
          <div class="font-semibold" v-show="editMode">
            Field Type*
          </div>
          <div class="flex flex-row">
            <div class="flex flex-row flex-wrap flex-1 my-1">
              <label for="field_type" class="font-semibold" v-show="!editMode">Field Type*</label>

              <select name="field_type"
                      id="field_type"
                      v-model="resourceForm.field_type"
                      :class="editMode ? editableClassesSelect : viewClassesSelect"
                      v-if="editMode">
                <template v-for="option in options.field_type">
                  <option v-if="(option.local_active === true && option.is_active === true)
                    || (option.value === resourceForm.field_type)"
                    :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>
              <div v-else>: {{
                  options.field_type[resourceForm.field_type] ? options.field_type[resourceForm.field_type].label : null
                }}
              </div>

              <div class="text-red">
                {{ alerts.getError('field_type') }}
              </div>
            </div>
          </div>
        </div>
        <div class="text-red">
          {{ alerts.getError('field_type') }}
        </div>
        <div class="flex flex-col flex-wrap flex-1 my-1">
          <div class="font-semibold">
            Is this the assigned intervention year?
          </div>
          <label v-if="editMode && admin">
            <input v-model="resourceForm.intervention_year" type="checkbox"
                   name="intervention_year"
                   value="1" class="mr-1">
            Yes
          </label>
          <div v-else>
            {{ resourceForm.intervention_year ? 'Yes' : 'No' }}
          </div>
        </div>

        <div @click="showSoilTest = (showSoilTest + 1) % 2" class="cursor-pointer flex flex-row mt-2">
        <span v-show="!showSoilTest" class="my-auto w-2">
          <i class="fas fa-caret-right"></i>
        </span>
          <span v-show="showSoilTest" class="my-auto w-2">
          <i class="fas fa-caret-down"></i>
        </span>
          <h4 class="text-lg font-semibold mx-4">Soil Test</h4>
          <span class="flex-1 my-auto">
          <hr>
        </span>
        </div>
        <div v-show="showSoilTest">
          <div class="flex flex-row flex-wrap flex-1 my-1 w-full">
            <label for="soil_profile_n" class="font-semibold">Soil Profile N (ppm)</label>
            <input v-model="resourceForm.soil_profile_n" name="soil_profile_n"
                   :disabled="!editMode"
                   :class="editMode ? editableClasses : viewClasses"
                   type="number"
                   min="0"
                   step=".01">
            <div class="text-red">
              {{ alerts.getError('soil_profile_n') }}
            </div>
          </div>

          <div class="flex flex-row flex-wrap flex-1 my-1 w-full">
            <label for="soil_profile_p" class="font-semibold">Soil Profile P (ppm)</label>
            <input v-model="resourceForm.soil_profile_p"
                   name="soil_profile_p"
                   :disabled="!editMode"
                   :class="editMode ? editableClasses : viewClasses"
                   type="number"
                   min="0"
                   step=".01">
            <div class="text-red">
              {{ alerts.getError('soil_profile_p') }}
            </div>
          </div>

          <div class="flex flex-row flex-wrap flex-1 my-1 w-full">
            <label for="soil_profile_k" class="font-semibold">Soil Profile K (ppm)</label>
            <input v-model="resourceForm.soil_profile_k"
                   name="soil_profile_k"
                   :disabled="!editMode"
                   :class="editMode ? editableClasses : viewClasses"
                   type="number"
                   min="0"
                   step=".01">
            <div class="text-red">
              {{ alerts.getError('soil_profile_k') }}
            </div>
          </div>
        </div>

        <div class="text-red" v-html="alerts.getError('boundary')">
        </div>

        <div v-if="editMode" class="text-sm mt-4">
          <button
              :disabled="this.submitting"
              class="px-6 py-1 rounded-sm text-white inline-block shadow-lg bg-green hover:bg-green-dark focus:bg-green-darker font-bold mr-1"
              type="submit" name="field-year-submit">Save
          </button>
          <button
              :disabled="this.submitting"
              class="px-6 py-1 rounded-sm text-grey-dark inline-block shadow-lg bg-grey-lightest hover:bg-white focus:bg-white font-bold mr-1"
              type="button" @click="toggleEdit()">Cancel
          </button>
          <button
              v-if="!this.isCreating"
              :disabled="this.submitting"
              class="px-6 py-1 rounded-sm text-red-dark inline-block shadow-lg bg-red-lighter hover:bg-white focus:bg-white mt-2 font-bold mr-1"
              type="button" @click="confirmOpen(1)">Delete
          </button>
        </div>
      </div>
      <div class="hidden">
        <Geography
            v-if="editMode"
            name="boundary"
            id="field-year-boundary"
            :source="resourceForm.boundary && typeof (resourceForm.boundary) === 'object'? resourceForm.boundary : null"
            upload-url="/boundary/upload"
            ref="boundary-input"
            :mapbox-token="mapboxToken"
            :esri-token="esriToken"
            @updateGeography="updateBoundary"
        >
        </Geography>
      </div>
    </div>
  </form>
  <div v-if="!(this.confirmMethod === 0)" class="fixed flex justify-center items-center top-0 bottom-0 left-0 right-0" @click="this.confirmMethod = 0;">
    <div class="bg-secondary rounded tile-shadow p-4 w-1/6 text-center" @click.stop="">
      <div>Are you sure you wish to {{ this.confirmMethods[this.confirmMethod] }} this resource?</div>
      <button class="px-6 py-1 rounded-sm text-white inline-block shadow-lg bg-red hover:bg-red-dark focus:bg-red-darker mt-2 font-bold mr-1" @click="confirmClose(false)">
        Confirm
      </button>
      <button class="px-6 py-1 rounded-sm text-grey-dark inline-block shadow-lg bg-grey-lightest hover:bg-white focus:bg-white mt-2 font-bold mr-1" @click="confirmClose(true)">
        Cancel
      </button>
    </div>
  </div>
</template>
